import type { TelegramWebApp } from '../hooks/useTelegramWebApp';
import { clearParamToken } from '../utils/paramTokenStore';
import { isAdminRigDiagnosticsEnabled, setAdminRigDiagnosticsEnabled } from '../utils/adminRigDiagnostics';
import { recordMiniappConnectionDiagnostic } from '../utils/miniappConnectionDiagnostics';
import { isOroborosProductPath } from '../../oroboros/routes';
import { isWalletPrincipalIdString } from '../../utils/walletPrincipal';
import {
  isLocalMiniappHost,
  isMiniappLocalDevActive,
  markMiniappDevPlaneActive,
  MINIAPP_DEV_TELEGRAM_ID_KEY,
  clearMiniappDevPlaneActive,
  probeAndActivateMiniappDevPlane,
  getCachedDevTelegramId,
  buildDevPlaneTelegramUser,
  ensureLocalDevTelegramId,
} from '../utils/localDevHost';
import { formatMiniappFetchAbortError, isMiniappFetchAbortError, miniappFetch } from './miniappApiClient';
import {
  clearSessionAccessToken,
  isLiveSessionAccessToken,
  readSessionAccessToken,
  shouldSendTelegramInitData,
} from '../../utils/sessionAccessToken';
import {
  deferApiRequestsUntil,
  isMiniappApiDeferredError,
  shouldDeferApiRequest,
} from '../../utils/apiReachabilityGate';
import { resolveMaErrorFromApi } from '../../shared/maErrorCodes';
import { MINIAPP_CONNECTION_ERROR_CODES } from '../utils/miniappConnectionErrorCodes';
import {
  DEMO_MUTATION_ERROR,
  isDemoMutationEndpoint,
  isMiniAppDemoMode,
  resolveDemoReadEndpoint,
} from '../utils/miniAppDemoMode';

type JsonObject = Record<string, any>;

function attachMaCode<T extends Record<string, unknown>>(
  payload: T,
  httpStatus?: number,
): T & { maCode?: string } {
  if (payload?.success !== false) return payload as T & { maCode?: string };
  const resolved = resolveMaErrorFromApi(payload as any, httpStatus);
  return {
    ...payload,
    maCode: String((payload as any)?.maCode || resolved.maCode),
  };
}

export type MiniAppApiResult<T> = T & { success: boolean; error?: string; maCode?: string; code?: string };

/** Pending intent from bot (Configure / Parameters); optional paramsHash8 for deep-link to matching combo. */
export type ParamComparePending = {
  strategyId: string;
  pair: string;
  timeframe: string;
  page?: 'param-compare' | 'backtest-params';
  paramsHash8?: string;
};

// ===== WEB AUTH HINT (non-secret, no PII) =====
// SECURITY: JWT stays in httpOnly `terminal_auth_token` only. We optionally
// persist a versioned TTL hint so the client can coalesce auth probes and UX
// without storing Telegram/provider IDs in sessionStorage (XSS-readable).
const WEB_AUTH_KEY = 'jester_web_auth';
const AUTH_STATE_KEY = 'jester_auth_state';
const WEB_AUTH_SCHEMA_V = 1;
const WEB_AUTH_HINT_TTL_MS = 8 * 60 * 60 * 1000;
const DEV_TELEGRAM_ID_KEY = MINIAPP_DEV_TELEGRAM_ID_KEY;

export interface WebAuthSessionHint {
  v: typeof WEB_AUTH_SCHEMA_V;
  exp: number;
}

export function getStoredWebAuth(): WebAuthSessionHint | null {
  try {
    const stored = sessionStorage.getItem(WEB_AUTH_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed?.v === WEB_AUTH_SCHEMA_V) {
      const exp = Number(parsed?.exp);
      if (Number.isFinite(exp) && Date.now() <= exp) {
        return { v: WEB_AUTH_SCHEMA_V, exp };
      }
    }
    sessionStorage.removeItem(WEB_AUTH_KEY);
    return null;
  } catch {
    try {
      sessionStorage.removeItem(WEB_AUTH_KEY);
    } catch {
      /* noop */
    }
    return null;
  }
}

/**
 * Mark that this tab recently validated a browser terminal session (cookie).
 * Optional id parameter is ignored — kept for call-site compatibility only.
 */
export function setStoredWebAuth(_userId?: number): void {
  try {
    sessionStorage.setItem(
      WEB_AUTH_KEY,
      JSON.stringify({
        v: WEB_AUTH_SCHEMA_V,
        exp: Date.now() + WEB_AUTH_HINT_TTL_MS,
      })
    );
    // A new browser session may be wallet-only. Drop stale auth probes that
    // previously concluded "requires web login" before the cookie existed.
    sessionStorage.removeItem(AUTH_STATE_KEY);
  } catch {}
}

export function clearStoredWebAuth(): void {
  try {
    sessionStorage.removeItem(WEB_AUTH_KEY);
    sessionStorage.removeItem(AUTH_STATE_KEY);
  } catch {}
}

/** Drop cached miniapp auth probe results after a fresh login (keep cookies). */
export function invalidateMiniAppAuthCache(): void {
  miniappAuthInFlight = null;
  miniappAuthLastResult = null;
  try {
    sessionStorage.removeItem(AUTH_STATE_KEY);
  } catch {}
}

export function clearMiniAppClientAuthState(): void {
  clearStoredWebAuth();
  void import('../mcpChatSessionStore')
    .then((m) => m.clearPersistedMcpChat())
    .catch(() => {});
  clearCachedDevTelegramId();
  setAdminRigDiagnosticsEnabled(false);
  devResolveMemoryCache = null;
  devResolveInflight = null;
  devConfigBackoffUntil = 0;
  miniappAuthInFlight = null;
  miniappAuthLastResult = null;
  clearParamToken();
  try {
    delete (window as any)?.JesterApp?.authToken;
  } catch {}
  clearSessionAccessToken();
  try {
    sessionStorage.removeItem(AUTH_STATE_KEY);
    sessionStorage.removeItem('jester_user_data');
    sessionStorage.removeItem('jester_miniapp_cache_v1');
    sessionStorage.removeItem('jester_portfolio_data');
    sessionStorage.removeItem('jester_strategies_summary');
  } catch {}
  try {
    localStorage.removeItem('jester_auth_token');
    localStorage.removeItem('jester_telegram_id');
    localStorage.removeItem('jester_user_wallet');
  } catch {}
}

let sessionExpiredBootScheduled = false;

/** True when this tab previously had a browser/terminal session worth treating as "expired" (not cold anonymous). */
function hadClientSessionHintBeforeExpiry(): boolean {
  if (getStoredWebAuth()) return true;
  try {
    if (readSessionAccessToken()) return true;
    if (String((window as any)?.JesterApp?.authToken || '').trim()) return true;
  } catch {}
  try {
    const raw = sessionStorage.getItem(AUTH_STATE_KEY);
    const o = raw ? JSON.parse(raw) : null;
    if (o?.success === true) return true;
  } catch {}
  try {
    if (String(localStorage.getItem('jester_telegram_id') || '').trim()) return true;
  } catch {}
  return false;
}

/**
 * Hard-boot to the marketing/login root after a dead session.
 * Idempotent; skips demo, guest, onboarding, Telegram WebView, and local miniapp dev plane.
 */
export function bootSessionExpiredToLogin(): boolean {
  if (typeof window === 'undefined') return false;
  if (sessionExpiredBootScheduled) return true;
  if (isMiniAppDemoMode()) return false;
  if (isMiniappLocalDevActive()) return false;
  // Telegram Mini App: keep in-webview UX (reopen / overlay); browser sessions go to `/`.
  try {
    if (String((window as any)?.Telegram?.WebApp?.initData || '').trim()) return false;
  } catch {}
  try {
    const path = (window.location.pathname || '/').toLowerCase();
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('guest') === '1') return false;
    if (params.has('pauth')) return false;
    if (params.get('forceLogin') === 'true') return false;
    if (path === '/' || path === '') return false;
    if (path === '/onboarding' || path.startsWith('/onboarding')) return false;
    // Oroboros owns its own sign-in overlay — never hard-boot to `/`.
    if (isOroborosProductPath(path)) return false;
  } catch {}

  sessionExpiredBootScheduled = true;
  clearMiniAppClientAuthState();
  // Do NOT call /session/logout here. A transient refresh failure (or expired
  // access JWT) must not revoke a still-valid remember-me refresh cookie —
  // that forced daily re-logins. Client state is cleared; next visit can
  // silently refresh again.
  try {
    window.location.replace('/');
  } catch {
    window.location.href = '/';
  }
  return true;
}

/** Clear auth and boot to `/` when a previously-authenticated session is gone. */
function handleMiniappSessionExpired(): void {
  const shouldBoot = hadClientSessionHintBeforeExpiry();
  if (shouldBoot) {
    bootSessionExpiredToLogin();
    return;
  }
  clearStoredWebAuth();
}

export async function miniappLogout(): Promise<{ success: boolean; error?: string }> {
  let error: string | undefined;
  try {
    const resp = await miniappFetch('/api/terminal/session/logout', {
      method: 'POST',
      headers: { 'X-Requested-With': 'Jester' },
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      error = String(body?.error || body?.message || `Logout failed (${resp.status})`);
    }
  } catch (e: any) {
    error = e?.message || 'Logout failed';
  } finally {
    clearMiniAppClientAuthState();
  }
  return error ? { success: false, error } : { success: true };
}

function clearCachedDevTelegramId(): void {
  clearMiniappDevPlaneActive();
}

function readCachedDevTelegramId(): number | null {
  return getCachedDevTelegramId();
}

export { getCachedDevTelegramId, buildDevPlaneTelegramUser, ensureLocalDevTelegramId };

type MiniappEndpointPair = { prod: string; dev: string };

async function resolveMiniappRequestAuth(
  telegram: TelegramWebApp,
  endpoints: MiniappEndpointPair,
  body: JsonObject,
): Promise<{ endpoint: string; headers: Record<string, string>; body: JsonObject; devTelegramId: number | null }> {
  const headers: Record<string, string> = {};
  let endpoint = endpoints.prod;
  let finalBody: JsonObject = { ...(body || {}) };

  if (isMiniAppDemoMode()) {
    const demoEndpoint = resolveDemoReadEndpoint(endpoints.prod) || resolveDemoReadEndpoint(endpoints.dev);
    if (demoEndpoint) {
      return { endpoint: demoEndpoint, headers, body: finalBody, devTelegramId: null };
    }
  }

  // Local admin plane is for unsigned localhost Mini App.
  // A live terminal session (Telegram widget / Artifice / password) must keep that principal.
  if (!isLiveSessionAccessToken()) {
    const localDevId = await resolveDevTelegramId();
    if (localDevId != null) {
      return {
        endpoint: endpoints.dev,
        headers,
        body: { ...finalBody, telegramId: localDevId },
        devTelegramId: localDevId,
      };
    }
  }

  if (telegram.isInTelegram && telegram.initData && shouldSendTelegramInitData(true)) {
    headers['x-telegram-init-data'] = telegram.initData;
  } else {
    const devTelegramId = await resolveDevTelegramId();
    if (devTelegramId) {
      endpoint = endpoints.dev;
      finalBody = { ...finalBody, telegramId: devTelegramId };
      return { endpoint, headers, body: finalBody, devTelegramId };
    }
  }

  return { endpoint, headers, body: finalBody, devTelegramId: null };
}

function readCachedAuthState(): { success: boolean; registered?: boolean | null; requiresWebLogin?: boolean } | null {
  try {
    const raw = sessionStorage.getItem(AUTH_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = Number(parsed?.ts || 0);
    if (!Number.isFinite(ts) || Date.now() - ts > 5 * 60 * 1000) return null;
    return {
      success: parsed?.success === true,
      registered: parsed?.registered === true ? true : parsed?.registered === false ? false : null,
      requiresWebLogin: parsed?.requiresWebLogin === true,
    };
  } catch {
    return null;
  }
}

function shouldProbeDevConfig(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('telegramId')) return true;
    if (readCachedDevTelegramId() != null) return true;
    if (isAdminRigDiagnosticsEnabled()) return true;
    if (isMiniappLocalDevActive()) return true;
    const path = (window.location.pathname || '').toLowerCase();
    if (path === '/miniapp' || path.startsWith('/miniapp/') || path.startsWith('/homedev/mini')) {
      return true;
    }
    return isLocalMiniappHost();
  } catch {
    return false;
  }
}

function useLocalDevTelegramIdFallback(): number | null {
  if (!isMiniappLocalDevActive() && !isLocalMiniappHost()) return null;
  return readCachedDevTelegramId();
}

export function hasMiniappProtectedApiSession(telegram?: TelegramWebApp): boolean {
  if (isMiniAppDemoMode()) return true;
  if (isLiveSessionAccessToken()) return true;
  if (isMiniappLocalDevActive() && readCachedDevTelegramId() != null) return true;
  if (telegram?.isInTelegram && !!telegram.initData && !isMiniappLocalDevActive()) return true;
  const auth = readCachedAuthState();
  if (auth?.success && auth.registered !== false && !auth.requiresWebLogin) return true;
  return !!getStoredWebAuth() || readCachedDevTelegramId() != null;
}

function readDevTelegramIdOverride(): number | null {
  try {
    // Allow explicit override via query string (?telegramId=123) for local dev.
    const params = new URLSearchParams(window.location.search);
    const q = params.get('telegramId');
    if (q && /^\d+$/.test(q)) return parseInt(q, 10);
  } catch {}
  return null;
}

/** Each miniappPost hit dev-config — cache + coalesce to avoid 429 / ERR_CONNECTION_REFUSED storms. */
const DEV_CONFIG_CACHE_OK_MS = 120_000;
const DEV_CONFIG_CACHE_NO_DEV_MS = 45_000;
const DEV_CONFIG_BACKOFF_SOFT_MS = 8_000;
const DEV_CONFIG_BACKOFF_429_MS = 15_000;

type DevResolveCache =
  | { kind: 'dev'; telegramId: number; expiresAt: number }
  | { kind: 'noDev'; expiresAt: number };

let devResolveMemoryCache: DevResolveCache | null = null;
let devResolveInflight: Promise<number | null> | null = null;
let devConfigBackoffUntil = 0;

async function resolveDevTelegramId(): Promise<number | null> {
  const override = readDevTelegramIdOverride();
  if (override != null) return override;
  if (!shouldProbeDevConfig()) return null;

  const now = Date.now();
  if (shouldDeferApiRequest()) {
    return useLocalDevTelegramIdFallback() ?? (isAdminRigDiagnosticsEnabled() ? readCachedDevTelegramId() : null);
  }
  if (now < devConfigBackoffUntil) {
    return useLocalDevTelegramIdFallback() ?? (isAdminRigDiagnosticsEnabled() ? readCachedDevTelegramId() : null);
  }

  if (devResolveMemoryCache && devResolveMemoryCache.expiresAt > now) {
    if (devResolveMemoryCache.kind === 'dev') {
      if (isMiniappLocalDevActive() || isLocalMiniappHost()) return devResolveMemoryCache.telegramId;
      return isAdminRigDiagnosticsEnabled() ? devResolveMemoryCache.telegramId : null;
    }
    return null;
  }

  if (devResolveInflight) return devResolveInflight;

  devResolveInflight = (async (): Promise<number | null> => {
    try {
      const resp = await miniappFetch('/api/miniapp/dev-config', { method: 'GET' });
      const json = await resp.json().catch(() => ({}));

      if (resp.status === 429) {
        devConfigBackoffUntil = Date.now() + DEV_CONFIG_BACKOFF_429_MS;
        return useLocalDevTelegramIdFallback() ?? (isAdminRigDiagnosticsEnabled() ? readCachedDevTelegramId() : null);
      }

      if (!resp.ok) {
        devConfigBackoffUntil = Date.now() + DEV_CONFIG_BACKOFF_SOFT_MS;
        return useLocalDevTelegramIdFallback() ?? (isAdminRigDiagnosticsEnabled() ? readCachedDevTelegramId() : null);
      }

      if (json?.success && json?.devMode && json?.adminId && Number.isFinite(json.adminId)) {
        const adminId = Number(json.adminId);
        setAdminRigDiagnosticsEnabled(true);
        markMiniappDevPlaneActive(adminId);
        devResolveMemoryCache = { kind: 'dev', telegramId: adminId, expiresAt: Date.now() + DEV_CONFIG_CACHE_OK_MS };
        return adminId;
      }

      clearCachedDevTelegramId();
      setAdminRigDiagnosticsEnabled(false);
      devResolveMemoryCache = { kind: 'noDev', expiresAt: Date.now() + DEV_CONFIG_CACHE_NO_DEV_MS };
      return null;
    } catch {
      devConfigBackoffUntil = Date.now() + DEV_CONFIG_BACKOFF_SOFT_MS;
      deferApiRequestsUntil(devConfigBackoffUntil);
      return useLocalDevTelegramIdFallback() ?? (isAdminRigDiagnosticsEnabled() ? readCachedDevTelegramId() : null);
    } finally {
      devResolveInflight = null;
    }
  })();

  return devResolveInflight;
}

export type MiniAppAuthResponse = {
  success: boolean;
  authenticated?: boolean;
  registered?: boolean;
  telegramId?: number;
  token?: string;
  message?: string;
  requiresOnboarding?: boolean;
  /** User is signed in but has not finished HL trading-key / builder onboarding. */
  requiresHyperliquidOnboarding?: boolean;
  requiresWebLogin?: boolean;
  devMode?: boolean;
  /** True when server runs with ADMIN_RIG=1 (admin rig / dev plane). */
  adminRig?: boolean;
  error?: string;
};

const MINIAPP_AUTH_DEDUPE_MS = 1000;
let miniappAuthInFlight: Promise<MiniAppAuthResponse> | null = null;
let miniappAuthLastResult:
  | {
      key: string;
      ts: number;
      result: MiniAppAuthResponse;
    }
  | null = null;

function rememberAdminRigFromAuth(result: MiniAppAuthResponse | null | undefined): void {
  if (!result || typeof result.adminRig !== 'boolean') return;
  setAdminRigDiagnosticsEnabled(result.adminRig);
}

function maybeRedirectAfterMiniAppAuth(result: MiniAppAuthResponse): void {
  if (!result.success || result.devMode || result.adminRig) return;
  if (!result.requiresHyperliquidOnboarding) return;

  // Product rule: MiniApp background auth should not send normal TG-backed users
  // to onboarding. The exception is wallet-only identities, which do need the HL
  // setup flow until their trading key/builder authorization exists.
  const isWalletOnly = isWalletPrincipalIdString(String(result.telegramId || ''));
  if (!isWalletOnly) return;

  maybeRedirectWalletOnlyToHyperliquidOnboarding(
    result.telegramId,
    result.requiresHyperliquidOnboarding,
    {
    allowMiniApp: true,
    source: 'miniapp',
    action: 'wallet_only_hl_setup',
  });
}

/** Local ADMIN_RIG dev plane (loopback API only). Used before Telegram/web auth on localhost. */
export async function authenticateLocalDevPlane(): Promise<MiniAppAuthResponse | null> {
  // Fast path: one POST — server defaults telegramId to primary ADMIN_ID when omitted.
  try {
    const cached = readCachedDevTelegramId();
    const resp = await miniappFetch('/api/miniapp/dev-auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'Jester' },
      body: JSON.stringify(cached ? { telegramId: cached } : {}),
    });
    const result = (await resp.json()) as MiniAppAuthResponse;
    rememberAdminRigFromAuth(result);
    if (result.success && result.telegramId) {
      markMiniappDevPlaneActive(result.telegramId);
      setStoredWebAuth();
      return result;
    }
  } catch {
    /* fall through to probe */
  }

  if (!readCachedDevTelegramId()) {
    await probeAndActivateMiniappDevPlane();
  }
  const devTelegramId = await resolveDevTelegramId();
  if (!devTelegramId) return null;
  try {
    const resp = await miniappFetch('/api/miniapp/dev-auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'Jester' },
      body: JSON.stringify({ telegramId: devTelegramId }),
    });
    const result = (await resp.json()) as MiniAppAuthResponse;
    rememberAdminRigFromAuth(result);
    if (result.success && result.telegramId) {
      markMiniappDevPlaneActive(result.telegramId);
      setStoredWebAuth();
    }
    return result;
  } catch {
    return null;
  }
}

function getMiniAppAuthCacheKey(telegram: TelegramWebApp): string {
  const forceLogin = (() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('forceLogin') === 'true' ? 'force' : 'normal';
    } catch {
      return 'normal';
    }
  })();
  const webAuth = getStoredWebAuth();
  return JSON.stringify({
    forceLogin,
    isInTelegram: telegram.isInTelegram,
    hasInitData: !!telegram.initData,
    liveTerminalSession: isLiveSessionAccessToken(),
    browserWebSessionHint: !!webAuth,
  });
}

export async function miniappAuthenticate(telegram: TelegramWebApp): Promise<MiniAppAuthResponse> {
  const cacheKey = getMiniAppAuthCacheKey(telegram);
  if (
    miniappAuthLastResult &&
    miniappAuthLastResult.key === cacheKey &&
    Date.now() - miniappAuthLastResult.ts < MINIAPP_AUTH_DEDUPE_MS
  ) {
    return miniappAuthLastResult.result;
  }

  if (miniappAuthInFlight) {
    return miniappAuthInFlight;
  }

  miniappAuthInFlight = (async () => {
    try {
      if (isMiniAppDemoMode()) {
        return {
          success: true,
          registered: true,
          demoMode: true,
          requiresWebLogin: false,
        } as MiniAppAuthResponse;
      }

      console.log('[miniappAuth] Starting authentication...');
      console.log('[miniappAuth] telegram.isInTelegram:', telegram.isInTelegram);
      console.log('[miniappAuth] telegram.initData:', telegram.initData ? 'present' : 'empty');
      
      // Check for ?forceLogin=true query param (for testing web login flow)
      const urlParams = new URLSearchParams(window.location.search);
      const forceLogin = urlParams.get('forceLogin');
      console.log('[miniappAuth] forceLogin param:', forceLogin);
      
      if (forceLogin === 'true') {
        console.log('[miniappAuth] Force login mode - showing web login');
        clearStoredWebAuth();
        return { success: false, error: 'Forced login mode', requiresWebLogin: true };
      }

      // Prefer dev plane first on miniapp (Cursor/Telegram WebView stubs often ship invalid initData).
      if (shouldProbeDevConfig()) {
        const localDev = await authenticateLocalDevPlane();
        if (localDev?.success) {
          console.log('[miniappAuth] Local dev plane auth OK');
          return localDev;
        }
        if (localDev && !localDev.success) {
          console.warn('[miniappAuth] Local dev plane auth failed:', localDev.error || localDev.message);
        }
      }

      // Priority 1: Running in Telegram — skip when this tab already has a live password/wallet JWT.
      if (telegram.isInTelegram && telegram.initData && shouldSendTelegramInitData(true)) {
        const resp = await miniappFetch('/api/miniapp/auth', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'Jester' },
          body: JSON.stringify({ initData: telegram.initData }),
        });
        const result = (await resp.json()) as MiniAppAuthResponse;
        rememberAdminRigFromAuth(result);
        if (resp.ok && result.success) {
          maybeRedirectAfterMiniAppAuth(result);
          return result;
        }

        // Telegram can keep stale WebApp initData alive across reloads until
        // the Mini App is fully closed. Try cookie auth below, then show the
        // approve-in-bot login overlay if the cookie is also gone.
        if (!isMiniappSessionAuthError(result?.error || result?.message)) {
          if (!isLocalMiniappHost()) return result;
          const localDev = await authenticateLocalDevPlane();
          if (localDev?.success) return localDev;
          return result;
        }
      }

      // Priority 2: httpOnly session cookie (browser) — validated by web-auth
      const resp = await miniappFetch('/api/miniapp/web-auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'Jester',
        },
        body: JSON.stringify({}),
      });
      const result = (await resp.json()) as MiniAppAuthResponse;
      rememberAdminRigFromAuth(result);
      if (result.success && resp.ok) {
        setStoredWebAuth();
        maybeRedirectAfterMiniAppAuth(result);
        return result;
      }
      if (isMiniappSessionAuthError(result?.error || result?.message)) {
        const refreshed = await attemptTerminalSessionRefresh();
        if (refreshed) {
          const retryResp = await miniappFetch('/api/miniapp/web-auth', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'Jester',
            },
            body: JSON.stringify({}),
          });
          const retryResult = (await retryResp.json()) as MiniAppAuthResponse;
          rememberAdminRigFromAuth(retryResult);
          if (retryResult.success && retryResp.ok) {
            setStoredWebAuth();
            maybeRedirectAfterMiniAppAuth(retryResult);
            return retryResult;
          }
        }
      }
      clearStoredWebAuth();
      if (resp.status === 403) {
        return {
          success: false,
          error: (result as any)?.error || 'Not allowed',
          requiresWebLogin: false,
        } as any;
      }
      // web-auth may return 200 { success: false } for no/invalid cookie — fall through to dev

      // Priority 3: Dev mode (local ADMIN_RIG / dev-config)
      const localDev = await authenticateLocalDevPlane();
      if (localDev) return localDev;

      // No auth available — on localhost use dev plane (ADMIN_RIG) instead of terminal login.
      if (isLocalMiniappHost()) {
        return {
          success: false,
          error: 'Dev auth unavailable. Start the local admin server and try again.',
          requiresWebLogin: false,
        };
      }
      return { success: false, error: 'Not authenticated', requiresWebLogin: true } as any;
    } catch (e: any) {
      return { success: false, error: e?.message || 'Network error' };
    }
  })();

  try {
    const result = await miniappAuthInFlight;
    miniappAuthLastResult = {
      key: cacheKey,
      ts: Date.now(),
      result,
    };
    return result;
  } finally {
    miniappAuthInFlight = null;
  }
}

export type MiniAppLaunchUrlResponse = {
  success: boolean;
  launchUrl?: string;
  botDeepLink?: string | null;
  telegramWebAppUrl?: string;
  error?: string;
};

export async function miniappGetLaunchUrl(): Promise<MiniAppLaunchUrlResponse> {
  try {
    const resp = await miniappFetch('/api/miniapp/launch-url', { method: 'GET' });
    return (await resp.json()) as MiniAppLaunchUrlResponse;
  } catch (e: any) {
    return { success: false, error: e?.message || 'Network error' };
  }
}

/** True when an API/message string indicates the user should re-authenticate or refresh the Mini App session. */
/** User-facing copy when a miniapp POST hits HTTP 429. */
export function formatMiniappRateLimitError(
  message: string | undefined | null,
  resetTime?: string | null,
): string {
  const base = String(message || '').trim();
  if (!/rate limit/i.test(base)) {
    return base || 'Too many requests. Please wait a moment.';
  }
  if (resetTime) {
    try {
      const resetMs = new Date(resetTime).getTime();
      if (Number.isFinite(resetMs) && resetMs > Date.now()) {
        const secs = Math.max(1, Math.ceil((resetMs - Date.now()) / 1000));
        if (secs < 90) {
          return `Too many requests — try again in ${secs} second${secs === 1 ? '' : 's'}.`;
        }
        const mins = Math.ceil(secs / 60);
        return `Too many requests — try again in about ${mins} minute${mins === 1 ? '' : 's'}.`;
      }
    } catch {
      /* ignore */
    }
  }
  return 'Too many requests — wait a moment, then try again.';
}

/** Silent session refresh via the shared refresh POST (same lock as Oroboros). */
export async function attemptTerminalSessionRefresh(): Promise<boolean> {
  try {
    const { fetchTerminalSessionRefresh } = await import('../../utils/terminalSessionRefresh');
    const { ok, data } = await fetchTerminalSessionRefresh();
    const success = !!(ok && data?.success && data?.token);
    if (success) {
      try {
        const wallet = String(data.walletAddress || '').trim();
        if (/^0x[a-fA-F0-9]{40}$/.test(wallet)) {
          const lower = wallet.toLowerCase();
          (window as any).JesterApp = (window as any).JesterApp || {};
          (window as any).JesterApp.userWallet = lower;
          localStorage.setItem('jester_user_wallet', lower);
        }
        setStoredWebAuth();
      } catch {
        /* noop */
      }
    }
    return success;
  } catch {
    return false;
  }
}

export function isMiniappSessionAuthError(message: string | undefined | null): boolean {
  const m = String(message || '').toLowerCase();
  if (!m) return false;
  // Jester session copy only. Do not match provider/LLM 401 "unauthorized"
  // — that used to wipe a live login after a bad xAI/OpenAI key test.
  return (
    m.includes('telegram session expired') ||
    m.includes('re-open the app from telegram') ||
    m.includes('session expired') ||
    m.includes('log in again') ||
    m.includes('sign-in required') ||
    m.includes('sign in required') ||
    m.includes('auth expired') ||
    m.includes('authentication required') ||
    m.includes('not authenticated')
  );
}

export async function miniappPost<T extends JsonObject>(
  telegram: TelegramWebApp,
  endpoints: { prod: string; dev: string },
  body: JsonObject = {},
  opts?: { timeoutMs?: number; recordDiagnostics?: boolean },
): Promise<MiniAppApiResult<T>> {
  const prodPath = endpoints.prod;
  const devPath = endpoints.dev;
  if (isDemoMutationEndpoint(prodPath) || isDemoMutationEndpoint(devPath)) {
    return { success: false, error: DEMO_MUTATION_ERROR, demoBlocked: true } as any;
  }

  const baseHeaders: Record<string, string> = { 'Content-Type': 'application/json', 'X-Requested-With': 'Jester' };
  const { endpoint, headers, body: finalBody } = await resolveMiniappRequestAuth(telegram, endpoints, body || {});
  const mergedHeaders = { ...baseHeaders, ...headers };

  const SERVICE_UNAVAILABLE_MSG = 'Service temporarily unavailable. Please try again later.';

  const recordApiFailure = (summary: string) => {
    if (opts?.recordDiagnostics === false) return;
    recordMiniappConnectionDiagnostic({ source: 'api', summary });
  };

  const timeoutMs =
    typeof opts?.timeoutMs === 'number' && Number.isFinite(opts.timeoutMs) && opts.timeoutMs > 0
      ? opts.timeoutMs
      : undefined;

  const executePost = async (allowSessionRefreshRetry: boolean): Promise<MiniAppApiResult<T>> => {
    const response = await miniappFetch(endpoint, {
      method: 'POST',
      headers: mergedHeaders,
      body: JSON.stringify(finalBody),
      ...(timeoutMs != null ? { timeoutMs } : {}),
    });

    const json = await response.json().catch(() => null);
    if (!json) {
      const status = response.status;
      if (status === 0 || (status >= 500 && status < 600)) {
        recordApiFailure(`POST ${endpoint} → HTTP ${status || 'network error'} (no JSON body)`);
        return attachMaCode({
          success: false,
          error: SERVICE_UNAVAILABLE_MSG,
          maCode: MINIAPP_CONNECTION_ERROR_CODES.API_EMPTY_RESPONSE,
        }, status) as any;
      }
      recordApiFailure(`POST ${endpoint} → invalid JSON (HTTP ${status})`);
      return attachMaCode({ success: false, error: `Invalid JSON response from ${endpoint}` }, status) as any;
    }

    if (response.status === 429) {
      devConfigBackoffUntil = Date.now() + DEV_CONFIG_BACKOFF_429_MS;
      const msg = formatMiniappRateLimitError(
        (json as any)?.message || (json as any)?.error,
        (json as any)?.resetTime,
      );
      recordApiFailure(`POST ${endpoint} → HTTP 429: ${msg}`);
      return attachMaCode({ success: false, error: msg, resetTime: (json as any)?.resetTime, code: (json as any)?.code }, 429) as any;
    }

    const authErrMsg = String((json as any)?.error || (json as any)?.message || '');
    const looksLikeSessionExpired =
      response.status === 401 ||
      (response.status === 200 &&
        (json as any)?.success === false &&
        isMiniappSessionAuthError(authErrMsg));

    if (looksLikeSessionExpired) {
      if (allowSessionRefreshRetry) {
        const refreshed = await attemptTerminalSessionRefresh();
        if (refreshed) {
          return executePost(false);
        }
      }
      handleMiniappSessionExpired();
      recordApiFailure(`POST ${endpoint} → HTTP ${response.status} (session expired)`);
      return attachMaCode({ success: false, error: 'Session expired. Please log in again.', code: 'auth_missing' }, response.status === 401 ? 401 : undefined) as any;
    }

    if (response.status >= 500) {
      const serverErr = String((json as any)?.error || (json as any)?.message || '').trim();
      recordApiFailure(
        serverErr
          ? `POST ${endpoint} → HTTP ${response.status}: ${serverErr}`
          : `POST ${endpoint} → HTTP ${response.status}`,
      );
    }

    return attachMaCode(json as MiniAppApiResult<T>, response.status);
  };

  try {
    const posted = await executePost(true);
    const pollUrl = String((posted as any)?.pollUrl || '');
    const jobId = String((posted as any)?.jobId || '');
    const st = String((posted as any)?.status || '');
    if (jobId && pollUrl && (st === 'queued' || st === 'running')) {
      try {
        const polled = await pollMiniappBacktestJobWithHeaders(pollUrl, mergedHeaders, {
          timeoutMs: 20 * 60_000,
        });
        return attachMaCode(polled as MiniAppApiResult<T>, 200);
      } catch (e: any) {
        recordApiFailure(`POST ${endpoint} job poll failed: ${e?.message || 'timeout'}`);
        return attachMaCode({ success: false, error: e?.message || 'Backtest timed out' } as any);
      }
    }
    return posted;
  } catch (e: any) {
    if (isMiniappApiDeferredError(e)) {
      return attachMaCode({
        success: false,
        error: SERVICE_UNAVAILABLE_MSG,
        maCode: MINIAPP_CONNECTION_ERROR_CODES.API_DEFERRED_BACKOFF,
      }) as any;
    }
    const msg = e?.message || '';
    if (isMiniappFetchAbortError(e)) {
      const friendly = formatMiniappFetchAbortError(timeoutMs ?? 30_000);
      recordApiFailure(`POST ${endpoint} failed: ${msg || 'aborted'} (client timeout)`);
      return attachMaCode({ success: false, error: friendly }) as any;
    }
    if (msg.includes('fetch') || msg.includes('network') || msg.includes('Failed') || msg.includes('Load') || msg.includes('Connection')) {
      recordApiFailure(`POST ${endpoint} failed: ${msg || 'network error'}`);
      return attachMaCode({
        success: false,
        error: SERVICE_UNAVAILABLE_MSG,
        maCode: MINIAPP_CONNECTION_ERROR_CODES.API_NETWORK,
      }) as any;
    }
    recordApiFailure(`POST ${endpoint} failed: ${msg || 'unknown error'}`);
    return attachMaCode({ success: false, error: msg || 'Network error' }) as any;
  }
}

/**
 * Poll GET /api/miniapp/backtest-jobs/:id until the queued backtest finishes (parameter tester / builder).
 * Uses the same auth headers as the initiating POST (initData, Bearer, cookies).
 */
export async function pollMiniappBacktestJobWithHeaders(
  pollUrl: string,
  headers: Record<string, string>,
  opts?: { intervalMs?: number; timeoutMs?: number }
): Promise<any> {
  const interval = opts?.intervalMs ?? 480;
  const timeout = opts?.timeoutMs ?? 10 * 60_000;
  const deadline = Date.now() + timeout;
  const getHeaders = { ...headers };
  delete getHeaders['Content-Type'];
  while (Date.now() < deadline) {
    const pr = await miniappFetch(pollUrl, { method: 'GET', headers: getHeaders });
    const pj = await pr.json().catch(() => ({}));
    if (pj.status === 'done') {
      if (pj.success === false) throw new Error(pj.error || 'Backtest failed');
      return pj;
    }
    if (pj.status === 'error') throw new Error(pj.error || 'Backtest failed');
    if (pj.success === false && pj.status !== 'queued' && pj.status !== 'running') {
      throw new Error(pj.error || 'Backtest failed');
    }
    await new Promise((r) => setTimeout(r, interval));
  }
  throw new Error('Backtest timed out');
}

// For downloads (CSV exports, etc) where the response is NOT JSON.
export async function miniappPostRaw(
  telegram: TelegramWebApp,
  endpoints: { prod: string; dev: string },
  body: JsonObject = {},
  opts?: { signal?: AbortSignal; timeoutMs?: number },
): Promise<Response> {
  if (isDemoMutationEndpoint(endpoints.prod) || isDemoMutationEndpoint(endpoints.dev)) {
    return new Response(JSON.stringify({ success: false, error: DEMO_MUTATION_ERROR, demoBlocked: true }), {
      status: 403,
      headers: { 'Content-Type': 'application/json' },
    });
  }
  const baseHeaders: Record<string, string> = { 'Content-Type': 'application/json', 'X-Requested-With': 'Jester' };
  const { endpoint, headers, body: finalBody } = await resolveMiniappRequestAuth(telegram, endpoints, body || {});

  return miniappFetch(endpoint, {
    method: 'POST',
    headers: { ...baseHeaders, ...headers },
    body: JSON.stringify(finalBody),
    signal: opts?.signal,
    ...(opts?.timeoutMs != null ? { timeoutMs: opts.timeoutMs } : {}),
  });
}

/** GET request with miniapp auth headers (initData, Bearer, or dev). */
export async function miniappGet<T extends JsonObject>(
  telegram: TelegramWebApp,
  url: string,
  opts?: { useDevPath?: boolean; timeoutMs?: number }
): Promise<MiniAppApiResult<T>> {
  const headers: Record<string, string> = {};
  const localDevId = isMiniappLocalDevActive() ? await resolveDevTelegramId() : null;
  if (localDevId != null) {
    const q = new URLSearchParams({ telegramId: String(localDevId) });
    url = `${url}${url.includes('?') ? '&' : '?'}${q.toString()}`;
  } else if (telegram.isInTelegram && telegram.initData && shouldSendTelegramInitData(true)) {
    headers['x-telegram-init-data'] = telegram.initData;
  } else if (opts?.useDevPath) {
    const devId = await resolveDevTelegramId();
    if (devId) {
      const q = new URLSearchParams({ telegramId: String(devId) });
      url = `${url}${url.includes('?') ? '&' : '?'}${q.toString()}`;
    }
  }
  const timeoutMs =
    typeof opts?.timeoutMs === 'number' && Number.isFinite(opts.timeoutMs) && opts.timeoutMs > 0
      ? opts.timeoutMs
      : undefined;
  const SERVICE_UNAVAILABLE_MSG = 'Service temporarily unavailable. Please try again later.';
  try {
    const executeGet = async (allowSessionRefreshRetry: boolean): Promise<MiniAppApiResult<T>> => {
      const resp = await miniappFetch(url, {
        method: 'GET',
        headers,
        ...(timeoutMs != null ? { timeoutMs } : {}),
      });
      const json = await resp.json().catch(() => null);
      const authErrMsg = String((json as any)?.error || (json as any)?.message || '');
      const looksLikeSessionExpired =
        resp.status === 401 ||
        (resp.status === 200 &&
          json &&
          (json as any)?.success === false &&
          isMiniappSessionAuthError(authErrMsg));

      if (looksLikeSessionExpired) {
        if (allowSessionRefreshRetry) {
          const refreshed = await attemptTerminalSessionRefresh();
          if (refreshed) {
            return executeGet(false);
          }
        }
        handleMiniappSessionExpired();
        return { success: false, error: 'Session expired. Please log in again.' } as any;
      }

      if (!json) {
        if (resp.status === 0 || (resp.status >= 500 && resp.status < 600)) {
          recordMiniappConnectionDiagnostic({
            source: 'api',
            summary: `GET ${url} → HTTP ${resp.status || 'network error'} (no JSON body)`,
          });
          return attachMaCode({
            success: false,
            error: SERVICE_UNAVAILABLE_MSG,
            maCode: MINIAPP_CONNECTION_ERROR_CODES.API_EMPTY_RESPONSE,
          }, resp.status) as any;
        }
        return { success: false, error: 'Invalid response' } as any;
      }
      if (resp.status === 429) {
        const msg = formatMiniappRateLimitError(
          (json as any)?.message || (json as any)?.error,
          (json as any)?.resetTime,
        );
        recordMiniappConnectionDiagnostic({ source: 'api', summary: `GET ${url} → HTTP 429: ${msg}` });
        return attachMaCode(
          { success: false, error: msg, resetTime: (json as any)?.resetTime, code: (json as any)?.code },
          429,
        ) as any;
      }
      if (resp.status >= 500) {
        const serverErr = String((json as any)?.error || (json as any)?.message || '').trim();
        recordMiniappConnectionDiagnostic({
          source: 'api',
          summary: serverErr
            ? `GET ${url} → HTTP ${resp.status}: ${serverErr}`
            : `GET ${url} → HTTP ${resp.status}`,
        });
        return attachMaCode({
          ...(json as object),
          success: false,
          error: serverErr || SERVICE_UNAVAILABLE_MSG,
        }, resp.status) as any;
      }
      return json as MiniAppApiResult<T>;
    };

    return await executeGet(true);
  } catch (e: any) {
    if (isMiniappApiDeferredError(e)) {
      return { success: false, error: SERVICE_UNAVAILABLE_MSG, maCode: MINIAPP_CONNECTION_ERROR_CODES.API_DEFERRED_BACKOFF } as any;
    }
    const msg = e?.message || '';
    if (isMiniappFetchAbortError(e)) {
      const friendly = formatMiniappFetchAbortError(timeoutMs ?? 30_000);
      recordMiniappConnectionDiagnostic({
        source: 'api',
        summary: `GET ${url} failed: ${msg || 'aborted'} (client timeout)`,
      });
      return attachMaCode({ success: false, error: friendly }) as any;
    }
    if (msg.includes('fetch') || msg.includes('network') || msg.includes('Failed') || msg.includes('Load') || msg.includes('Connection')) {
      recordMiniappConnectionDiagnostic({ source: 'api', summary: `GET ${url} failed: ${msg || 'network error'}` });
      return attachMaCode({
        success: false,
        error: SERVICE_UNAVAILABLE_MSG,
        maCode: MINIAPP_CONNECTION_ERROR_CODES.API_NETWORK,
      }) as any;
    }
    recordMiniappConnectionDiagnostic({ source: 'api', summary: `GET ${url} failed: ${msg || 'unknown error'}` });
    return attachMaCode({ success: false, error: msg || 'Network error' }) as any;
  }
}

/** Fetch pending param-compare / backtest-params intent (for fallback when Telegram strips button URL). */
export async function fetchPendingParamCompare(telegram: TelegramWebApp): Promise<ParamComparePending | null> {
  try {
    const url = '/api/miniapp/pending-param-compare';
    const result = await miniappGet<{ pending: ParamComparePending | null }>(telegram, url);
    if (!result?.success || !result.pending) return null;
    return result.pending;
  } catch {
    return null;
  }
}


